import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  AddRounded,
  ArrowBackRounded,
  AssignmentIndOutlined,
  AssignmentTurnedInRounded,
  CommentOutlined,
  EditRounded,
  EventOutlined,
  ExpandMoreRounded,
  PersonOutlineRounded,
  PrecisionManufacturing,
  RefreshRounded,
  SearchRounded,
  TextSnippetOutlined
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { SmartDialog } from '../../../../generic/utilities/SmartDialog'
import { useAuthorization } from '../../../../generic/hooks/useAuthorization'
import Document from '../../../../configuration/document/Document'
import ActionExecutionDialog from '../../../../quality/ActionExecutionDialog'
import { systemValueCodeLabel } from '../../../../generic/systemValues/systemValueLabel'
import { QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS } from '../qualityIncidencePriority'

const IncidenceListItem = React.memo(function IncidenceListItem ({
  incidence,
  isSelected,
  onSelectUuid,
  formatDate,
  t_,
  priorityColorByValue,
  theme,
  isSmallScreen,
  onNavigateToDetails
}) {
  const incidenceUuid = incidence?.uuid ?? null
  const incidenceStatus = String(incidence?.status || 'OPEN').trim().toUpperCase()
  const incidencePriority = String(incidence?.priority || 'MEDIUM').trim().toUpperCase()
  const incidenceLabel = String(incidence?.title || incidence?.code || '-').trim()
  const incidenceCode = String(incidence?.code || '').trim()

  const priorityColor = priorityColorByValue.get(incidencePriority) || theme.palette.text.secondary
  const ariaLabel = incidenceCode ? `${incidenceCode} ${incidenceLabel}` : incidenceLabel

  return (
    <ListItemButton
      selected={isSelected}
      onClick={() => {
        onSelectUuid(incidenceUuid)
        if (isSmallScreen) onNavigateToDetails()
      }}
      sx={{
        contentVisibility: 'auto',
        containIntrinsicSize: '96px',
        border: `1px solid ${alpha(isSelected ? theme.palette.success.main : theme.palette.common.white, isSelected ? 0.45 : 0.12)}`,
        backgroundColor: isSelected ? alpha(theme.palette.success.main, 0.08) : alpha('#000', 0.08),
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        alignItems: 'flex-start'
      }}
      aria-label={ariaLabel}
      aria-selected={isSelected}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ width: '100%' }} spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='caption' sx={{ color: isSelected ? 'success.light' : 'text.secondary', fontWeight: 800 }}>
            {systemValueCodeLabel(t_, 'QualityIncidenceStatusEnum', incidenceStatus)}
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
            {incidenceCode ? `${incidenceCode} · ${incidenceLabel}` : incidenceLabel}
          </Typography>
          <Typography variant='caption' sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
            {incidence?.createdDate ? formatDate(incidence.createdDate) : t_('No date')}
          </Typography>
        </Box>
        <Stack spacing={0.5} alignItems='flex-end' sx={{ flexShrink: 0 }}>
          <Chip
            label={systemValueCodeLabel(t_, 'QualityIncidencePriorityEnum', incidencePriority, QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS)}
            size='small'
            sx={{
              border: `1px solid ${alpha(priorityColor, 0.35)}`,
              backgroundColor: alpha(priorityColor, 0.12),
              color: priorityColor,
              fontWeight: 800
            }}
          />
          {incidence?.hasAction && (
            <Chip
              icon={<AssignmentTurnedInRounded sx={{ fontSize: '12px !important' }} />}
              label={t_('Actions')}
              size='small'
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 800,
                border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                backgroundColor: alpha(theme.palette.info.main, 0.12),
                color: theme.palette.info.main,
                '& .MuiChip-icon': {
                  color: 'inherit',
                  ml: '4px'
                }
              }}
            />
          )}
        </Stack>
      </Stack>
    </ListItemButton>
  )
}, (prev, next) => {
  const prevIncidence = prev.incidence || {}
  const nextIncidence = next.incidence || {}

  return (
    prev.isSelected === next.isSelected &&
    prevIncidence?.uuid === nextIncidence?.uuid &&
    prevIncidence?.status === nextIncidence?.status &&
    prevIncidence?.priority === nextIncidence?.priority &&
    prevIncidence?.title === nextIncidence?.title &&
    prevIncidence?.code === nextIncidence?.code &&
    prevIncidence?.createdDate === nextIncidence?.createdDate &&
    prevIncidence?.hasAction === nextIncidence?.hasAction
  )
})

const ReadOnlyField = React.memo(function ReadOnlyField ({ label, value, theme }) {
  return (
    <Box
      sx={{
        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        borderRadius: 1.5,
        backgroundColor: alpha('#000', 0.12),
        px: 1.5,
        py: 1.25,
        minHeight: 56
      }}
    >
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ fontWeight: 700, mt: 0.25, wordBreak: 'break-word' }}>
        {String(value ?? '').trim() || '-'}
      </Typography>
    </Box>
  )
})

const ActionCard = ({ action, theme, formatDate, t_, onExecute, onOpenActionDocuments, canViewDocuments = false, isChild = false }) => {
  const isPending = action.actionStatus === 'PENDING'
  const statusColor = isPending ? theme.palette.warning.main : theme.palette.success.main
  const actionResult = String(action.actionResult || '').trim().toUpperCase()
  const isRejected = actionResult === 'NO_OK'
  const isAccepted = actionResult === 'OK'
  const resultColor = isRejected ? theme.palette.error.main : theme.palette.success.main
  // A parent action only groups its sub-actions; execution (accept/reject) is done on each leaf action.
  const hasChildren = Array.isArray(action.children) && action.children.length > 0
  // Once an action is done (MADE) it can no longer be executed from MOS.
  const isMade = String(action.actionStatus || '').trim().toUpperCase() === 'MADE'
  const canExecute = Boolean(onExecute) && !hasChildren && !isMade

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        backgroundColor: isChild ? alpha(theme.palette.common.white, 0.01) : alpha(theme.palette.common.white, 0.03),
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        borderLeft: `3px solid ${alpha(statusColor, 0.6)}`,
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          backgroundColor: isChild ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.white, 0.05),
          borderColor: alpha(theme.palette.common.white, 0.12)
        }
      }}
    >
      <Box sx={{ p: 1.5, flexGrow: 1, minWidth: 0 }}>
        {/* Top file row: code,type,status,date */}
        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2} sx={{ mb: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 800, color: theme.palette.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {action.code ? `${action.code} · ` : ''}{t_(action.actionType)}
            </Typography>
            <Chip
              label={t_(action.actionStatus)}
              size='small'
              sx={{
                height: 16,
                fontSize: '0.6rem',
                fontWeight: 900,
                backgroundColor: alpha(statusColor, 0.08),
                color: statusColor,
                border: `1px solid ${alpha(statusColor, 0.15)}`,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}
            />
            {(isAccepted || isRejected) && (
              <Chip
                label={actionResult}
                size='small'
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 900,
                  backgroundColor: alpha(resultColor, 0.08),
                  color: resultColor,
                  border: `1px solid ${alpha(resultColor, 0.15)}`,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              />
            )}
            {action.resourceName && (
              <Stack direction='row' spacing={0.5} alignItems='center' sx={{ ml: 1, opacity: 0.8 }}>
                <PrecisionManufacturing sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography variant='caption' sx={{ fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {action.resourceName}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {action.createDate ? formatDate(action.createDate) : (action.mailDate ? formatDate(action.mailDate) : '')}
          </Typography>
        </Stack>

        {/* Body: description,metadata */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='space-between' alignItems='flex-start'>
          <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ flexGrow: 1, minWidth: 0, maxWidth: { sm: '85%' } }}>
            {onOpenActionDocuments && (
              <Tooltip title={t_('Documents')}>
                <span>
                  <IconButton
                    disabled={!canViewDocuments || !action.uuid}
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenActionDocuments(action)
                    }}
                    aria-label={t_('Documents')}
                    sx={{
                      flexShrink: 0,
                      color: 'primary.main',
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.24),
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <TextSnippetOutlined fontSize='medium' />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.4,
                fontWeight: 400,
                fontSize: '1.1rem',
                opacity: 0.85,
                flexGrow: 1
              }}
            >
              {action.description || action.procedureText || '-'}
            </Typography>
          </Stack>

          <Stack direction='row' spacing={2} sx={{ flexShrink: 0, pt: { xs: 1, sm: 0 } }}>
            {(action.workerName || action.responsible) && (
              <Stack spacing={0.5}>
                {action.workerName && (
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <PersonOutlineRounded sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                    <Typography variant='caption' sx={{ fontWeight: 600, fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                      {action.workerName}
                    </Typography>
                  </Stack>
                )}
                {action.responsible && (
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <AssignmentIndOutlined sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                    <Typography variant='caption' sx={{ fontWeight: 600, fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                      {action.responsible}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            {(action.expirationDate || action.executionDate) && (
              <Stack spacing={0.5}>
                {action.expirationDate && (
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <EventOutlined sx={{ fontSize: 14, color: theme.palette.warning.dark }} />
                    <Typography variant='caption' sx={{ fontWeight: 700, fontSize: '0.75rem', color: theme.palette.warning.dark }}>
                      {formatDate(action.expirationDate)}
                    </Typography>
                  </Stack>
                )}
                {action.executionDate && (
                  <Stack direction='row' spacing={0.75} alignItems='center'>
                    <EventOutlined sx={{ fontSize: 14, color: theme.palette.success.main }} />
                    <Typography variant='caption' sx={{ fontWeight: 700, fontSize: '0.75rem', color: theme.palette.success.main }}>
                      {formatDate(action.executionDate)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* Comment inline small */}
        {action.comment && (
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.04)}` }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <CommentOutlined sx={{ fontSize: 20, color: theme.palette.text.disabled }} />
              <Typography variant='caption' sx={{ fontStyle: 'italic', color: theme.palette.text.disabled, fontSize: '1rem' }}>
                {action.comment}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Execution comment (entered when the action was confirmed/rejected) */}
        {action.executionComment && (
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.04)}` }}>
            <Stack direction='row' spacing={1} alignItems='flex-start'>
              <AssignmentTurnedInRounded sx={{ fontSize: 20, color: (isAccepted || isRejected) ? resultColor : theme.palette.text.secondary }} />
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontSize: '1rem' }}>
                <Box component='span' sx={{ fontWeight: 700, mr: 0.5 }}>{t_('Execution comment')}:</Box>
                {action.executionComment}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {canExecute && (
        <ButtonBase
          onClick={(e) => {
            e.stopPropagation()
            onExecute(action)
          }}
          sx={{
            width: 100,
            borderRadius: 0,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            color: theme.palette.primary.main,
            transition: 'all 0.2s',
            borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            px: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.light
            }
          }}
        >
          <Typography
            variant='caption'
            sx={{
              fontWeight: 900,
              textTransform: 'uppercase',
              textAlign: 'center',
              lineHeight: 1.2,
              letterSpacing: 0.5
            }}
          >
            {t_('Execute')}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  )
}

const QualityIncidenceHistoryView = ({
  t_,
  theme,
  isSmallScreen,
  historyPanel,
  setHistoryPanel,
  detailsTab,
  setDetailsTab,
  historyPanelsHeight,
  onOpenCreateForm,
  onOpenEditForm,
  canEditSelectedDraft,
  onCloseDialog,
  filteredOpenIncidences,
  selectedOpenIncidence,
  isLoadingOpenIncidences,
  isLoadingOpenIncidencesError,
  onRefreshOpenIncidences,
  incidenceSearchQuery,
  onIncidenceSearchQueryChange,
  onClearIncidenceSearchQuery,
  priorityFilter,
  priorityFilterOptions,
  onPriorityFilterChange,
  onClearFilters,
  priorityColorByValue,
  onSelectOpenIncidenceUuid,
  formatDate,
  selectedOpenIncidenceCode,
  selectedOpenIncidenceLabel,
  selectedOpenIncidenceStatus,
  selectedOpenIncidenceStatusColor,
  selectedOpenIncidencePriority,
  selectedOpenIncidencePriorityColor,
  selectedOpenIncidenceCreatedDate,
  selectedOpenIncidenceEndDate,
  selectedOpenIncidenceFabricationOrderName,
  selectedOpenIncidenceJobName,
  selectedOpenIncidenceDeviceName,
  selectedOpenIncidenceWorkerName,
  selectedOpenIncidenceResponsibleName,
  selectedOpenIncidenceDescription,
  selectedOpenIncidencePreliminaryAnalysis,
  selectedOpenIncidenceProposedSolution,
  isLoadingSelectedOpenIncidenceDetails,
  isLoadingSelectedOpenIncidenceDetailsError,
  selectedOpenIncidenceScopeRows,
  selectedOpenIncidenceActionRows,
  isLoadingSelectedOpenIncidenceActions,
  isLoadingSelectedOpenIncidenceActionsError,
  onActionExecuted
}) => {
  const [executeAction, setExecuteAction] = React.useState(null)
  const isUserAuthorized = useAuthorization()
  const canViewActionDocuments = isUserAuthorized('DOCUMENTS_QUALITY_INCIDENCE')
  const [showActionDocuments, setShowActionDocuments] = React.useState(false)
  const [documentsAction, setDocumentsAction] = React.useState({})
  const [isDownloadingActionDocument, setIsDownloadingActionDocument] = React.useState(false)
  const hasFilters = Boolean(incidenceSearchQuery) || priorityFilter !== 'ALL'

  const openActionDocuments = React.useCallback((action) => {
    if (!action?.uuid) return
    setDocumentsAction(action)
    setShowActionDocuments(true)
  }, [])
  const listCount = filteredOpenIncidences.length
  const noResults = listCount === 0 && hasFilters

  const handleActionExecuted = React.useCallback(() => {
    setExecuteAction(null)
    onActionExecuted?.()
  }, [onActionExecuted])

  const detailsHeader = useMemo(() => {
    const title = selectedOpenIncidenceCode ? `${selectedOpenIncidenceCode} · ${selectedOpenIncidenceLabel}` : selectedOpenIncidenceLabel
    const dateLabel = selectedOpenIncidenceCreatedDate ? formatDate(selectedOpenIncidenceCreatedDate) : t_('No date')
    return { title, dateLabel }
  }, [formatDate, selectedOpenIncidenceCode, selectedOpenIncidenceCreatedDate, selectedOpenIncidenceLabel, t_])

  const groupedActions = useMemo(() => {
    const list = Array.isArray(selectedOpenIncidenceActionRows) ? selectedOpenIncidenceActionRows : []
    const actionMap = new Map(list.map((a) => [a.uuid, a]))

    const parents = list.filter((a) => !a.actionParentUuid || !actionMap.has(a.actionParentUuid))
    const children = list.filter((a) => a.actionParentUuid && actionMap.has(a.actionParentUuid))

    return parents.flatMap((parent) => {
      const parentChildren = children.filter((child) => child.actionParentUuid === parent.uuid)
      // QI-level parents are pure grouping wrappers — the incidence's OF/job is informational
      // now, so the parent card has no real target. Drop it and surface its children directly
      // to avoid the duplicated "Mark as done" entry in the MOS history view. Scope-level
      // parents are kept because they DO carry their own target (the scope itself).
      if (parent.targetType === 'qualityIncidence' && parentChildren.length > 0) {
        return parentChildren.map((child) => ({ ...child, children: [] }))
      }
      return [{ ...parent, children: parentChildren }]
    })
  }, [selectedOpenIncidenceActionRows])

  return (
    <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h6' sx={{ fontWeight: 800 }}>
          {t_('Quality incidences')}
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button
            type='button'
            variant='outlined'
            color='primary'
            onClick={onOpenCreateForm}
            sx={{ borderColor: 'transparent' }}
          >
            <AddRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
              {t_('Add new quality incidence').toUpperCase()}
            </Typography>
          </Button>
          <Button
            type='button'
            variant='outlined'
            color='primary'
            onClick={onOpenEditForm}
            sx={{ borderColor: 'transparent' }}
            disabled={!canEditSelectedDraft}
          >
            <EditRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
              {t_('Edit').toUpperCase()}
            </Typography>
          </Button>
          <Button
            type='button'
            variant='outlined'
            color='primary'
            onClick={onCloseDialog}
            sx={{ borderColor: 'transparent' }}
          >
            <CloseRoundedIcon sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
              {t_('Close').toUpperCase()}
            </Typography>
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(18rem, 22rem) minmax(0, 1fr)',
          gap: 2,
          flexGrow: 1,
          minHeight: 0
        }}
      >
        {(isSmallScreen ? historyPanel === 'list' : true) && (
          <Box
            sx={{
              border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
              backgroundColor: alpha('#000', 0.18),
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: historyPanelsHeight,
              overflow: 'hidden'
            }}
          >
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
              <Typography variant='overline' sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: 1 }}>
                {t_('Operation incidences')}
              </Typography>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {listCount}
                </Typography>
                <IconButton
                  size='small'
                  onClick={onRefreshOpenIncidences}
                  disabled={isLoadingOpenIncidences}
                  aria-label={t_('Refresh')}
                >
                  <RefreshRounded fontSize='small' />
                </IconButton>
              </Stack>
            </Stack>

            <TextField
              value={incidenceSearchQuery}
              onChange={onIncidenceSearchQueryChange}
              size='small'
              placeholder={t_('Search')}
              aria-label={t_('Search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchRounded fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: incidenceSearchQuery
                  ? (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={onClearIncidenceSearchQuery} aria-label={t_('Clear')}>
                        <CloseRoundedIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                    )
                  : null
              }}
              sx={{
                mb: 1.25,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha(theme.palette.common.white, 0.08)
                }
              }}
            />

            <Stack direction='row' spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              {priorityFilterOptions.map((option) => {
                const selected = String(priorityFilter) === option.value
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    onClick={() => onPriorityFilterChange(option.value)}
                    size='small'
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: alpha(option.color, selected ? 0.6 : 0.25),
                      color: selected ? theme.palette.common.black : option.color,
                      backgroundColor: selected ? alpha(option.color, 0.85) : alpha('#000', 0.1),
                      fontWeight: 800
                    }}
                  />
                )
              })}
              {hasFilters && (
                <Chip
                  label={t_('Clear')}
                  onClick={onClearFilters}
                  size='small'
                  variant='outlined'
                  sx={{ borderColor: alpha(theme.palette.common.white, 0.2) }}
                />
              )}
            </Stack>

            <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08), mb: 1.5 }} />

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {isLoadingOpenIncidences
                ? (
                  <Box sx={{ minHeight: '10rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={22} />
                  </Box>
                  )
                : isLoadingOpenIncidencesError
                  ? (
                    <Box sx={{ minHeight: '10rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Stack spacing={1} alignItems='center'>
                        <Typography variant='body2' color='text.secondary' align='center'>
                          {t_('Error loading incidences')}
                        </Typography>
                        <Button type='button' variant='outlined' onClick={onRefreshOpenIncidences} sx={{ borderColor: 'transparent' }}>
                          <RefreshRounded sx={{ fontSize: '1.2em' }} />
                          <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                            {t_('Retry').toUpperCase()}
                          </Typography>
                        </Button>
                      </Stack>
                    </Box>
                    )
                  : listCount > 0
                    ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {filteredOpenIncidences.map((incidence) => {
                          const incidenceUuid = incidence?.uuid ?? null
                          const key = incidenceUuid ?? `${String(incidence?.code || '').trim()}-${String(incidence?.title || incidence?.name || '').trim()}`
                          const isSelected = incidenceUuid != null && incidenceUuid === selectedOpenIncidence?.uuid

                          return (
                            <IncidenceListItem
                              key={key}
                              incidence={incidence}
                              isSelected={isSelected}
                              onSelectUuid={onSelectOpenIncidenceUuid}
                              formatDate={formatDate}
                              t_={t_}
                              priorityColorByValue={priorityColorByValue}
                              theme={theme}
                              isSmallScreen={isSmallScreen}
                              onNavigateToDetails={() => setHistoryPanel('details')}
                            />
                          )
                        })}
                      </Box>
                      )
                    : (
                      <Box
                        sx={{
                          height: '100%',
                          minHeight: '10rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px dashed ${alpha(theme.palette.common.white, 0.12)}`,
                          borderRadius: 1.5,
                          p: 2
                        }}
                      >
                        <Typography variant='body2' color='text.secondary' align='center'>
                          {noResults ? t_('No results') : t_('No incidences available')}
                        </Typography>
                      </Box>
                      )}
            </Box>
          </Box>
        )}

        {(isSmallScreen ? historyPanel === 'details' : true) && (
          <Box
            sx={{
              border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
              backgroundColor: alpha('#000', 0.14),
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: historyPanelsHeight,
              overflow: 'hidden'
            }}
          >
            {selectedOpenIncidence
              ? (
                <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
                  <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
                      {isSmallScreen && (
                        <IconButton size='small' onClick={() => setHistoryPanel('list')} aria-label={t_('Back')}>
                          <ArrowBackRounded fontSize='small' />
                        </IconButton>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 800, lineHeight: 1.1, wordBreak: 'break-word' }}>
                          {detailsHeader.title}
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                          {detailsHeader.dateLabel}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Chip
                        label={systemValueCodeLabel(t_, 'QualityIncidenceStatusEnum', selectedOpenIncidenceStatus)}
                        size='small'
                        sx={{
                          border: `1px solid ${alpha(selectedOpenIncidenceStatusColor, 0.35)}`,
                          backgroundColor: alpha(selectedOpenIncidenceStatusColor, 0.12),
                          color: selectedOpenIncidenceStatusColor,
                          fontWeight: 800
                        }}
                      />
                      <Chip
                        label={systemValueCodeLabel(t_, 'QualityIncidencePriorityEnum', selectedOpenIncidencePriority, QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS)}
                        size='small'
                        sx={{
                          border: `1px solid ${alpha(selectedOpenIncidencePriorityColor, 0.35)}`,
                          backgroundColor: alpha(selectedOpenIncidencePriorityColor, 0.12),
                          color: selectedOpenIncidencePriorityColor,
                          fontWeight: 800
                        }}
                      />
                    </Stack>
                  </Stack>

                  {isLoadingSelectedOpenIncidenceDetails && (
                    <Stack direction='row' alignItems='center' gap={1}>
                      <CircularProgress size={18} />
                      <Typography variant='body2' color='text.secondary'>
                        {t_('Loading')}
                      </Typography>
                    </Stack>
                  )}

                  {isLoadingSelectedOpenIncidenceDetailsError && (
                    <Typography variant='body2' color='text.secondary'>
                      {t_('Error loading details')}
                    </Typography>
                  )}

                  <Tabs
                    value={detailsTab}
                    onChange={(_, nextValue) => setDetailsTab(nextValue)}
                    variant='scrollable'
                    scrollButtons='auto'
                    aria-label={t_('Incidence details tabs')}
                    sx={{
                      minHeight: 38,
                      '& .MuiTab-root': { minHeight: 38, fontWeight: 800 }
                    }}
                  >
                    <Tab value='overview' label={t_('Overview')} />
                    <Tab value='scopes' label={t_('Scopes')} />
                    <Tab value='actions' label={t_('Actions')} />
                  </Tabs>

                  <Box sx={{ flexGrow: 1, overflow: 'auto', pt: 1, minHeight: 0 }}>
                    {detailsTab === 'overview' && (
                      <Grid container spacing={2} alignItems='stretch'>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Title')} value={selectedOpenIncidenceLabel} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Code')} value={selectedOpenIncidenceCode} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Status')} value={systemValueCodeLabel(t_, 'QualityIncidenceStatusEnum', selectedOpenIncidenceStatus)} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Priority')} value={systemValueCodeLabel(t_, 'QualityIncidencePriorityEnum', selectedOpenIncidencePriority, QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS)} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Created date')} value={selectedOpenIncidenceCreatedDate ? formatDate(selectedOpenIncidenceCreatedDate) : ''} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('End date')} value={selectedOpenIncidenceEndDate ? formatDate(selectedOpenIncidenceEndDate) : ''} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Fabrication order (OF)')} value={selectedOpenIncidenceFabricationOrderName} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Job')} value={selectedOpenIncidenceJobName} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Device')} value={selectedOpenIncidenceDeviceName} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Worker')} value={selectedOpenIncidenceWorkerName} theme={theme} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ReadOnlyField label={t_('Responsible')} value={selectedOpenIncidenceResponsibleName} theme={theme} />
                        </Grid>
                        <Grid item xs={12}>
                          <Box component='fieldset' sx={{ borderColor: '#ffffff4d', borderRadius: 1, padding: '4px 14px 14px', minWidth: 0 }}>
                            <Box component='legend' sx={{ px: 1, fontSize: '0.75rem' }}>
                              {t_('Description')}
                            </Box>
                            <Box sx={{ minHeight: '5rem', color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {selectedOpenIncidenceDescription || '-'}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box component='fieldset' sx={{ borderColor: '#ffffff4d', borderRadius: 1, padding: '4px 14px 14px', minWidth: 0 }}>
                            <Box component='legend' sx={{ px: 1, fontSize: '0.75rem' }}>
                              {t_('Preliminary analysis')}
                            </Box>
                            <Box sx={{ minHeight: '4rem', color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {selectedOpenIncidencePreliminaryAnalysis || '-'}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box component='fieldset' sx={{ borderColor: '#ffffff4d', borderRadius: 1, padding: '4px 14px 14px', minWidth: 0 }}>
                            <Box component='legend' sx={{ px: 1, fontSize: '0.75rem' }}>
                              {t_('Proposed solution')}
                            </Box>
                            <Box sx={{ minHeight: '4rem', color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {selectedOpenIncidenceProposedSolution || '-'}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    {detailsTab === 'scopes' && (
                      <Stack spacing={1.5}>
                        {selectedOpenIncidenceScopeRows.length === 0
                          ? (
                            <Typography variant='body2' color='text.secondary'>
                              {t_('No scopes added')}
                            </Typography>
                            )
                          : (
                              selectedOpenIncidenceScopeRows.map((scopeRow) => {
                                const hasResources = scopeRow.resources.length > 0
                                return (
                                  <Box
                                    key={scopeRow.scopeRowId}
                                    sx={{
                                      border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.1),
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <Box sx={{ p: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                      <Grid container spacing={2} alignItems='center'>
                                        <Grid item xs={12} sm={6}>
                                          <Stack direction='row' spacing={1.25} alignItems='center'>
                                            <Box sx={{ px: 1, py: 0.5, borderRadius: 1, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                                              <Typography variant='caption' sx={{ fontWeight: 900, letterSpacing: 0.5 }}>OF</Typography>
                                            </Box>
                                            <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                                              {scopeRow.fabricationOrderCode || '-'}
                                            </Typography>
                                          </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <Stack direction='row' spacing={1} alignItems='center' justifyContent={isSmallScreen ? 'flex-start' : 'flex-end'}>
                                            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>{t_('JOB')}:</Typography>
                                            <Typography variant='body2' sx={{ fontWeight: 700 }}>
                                              {scopeRow.jobName || '-'}
                                            </Typography>
                                          </Stack>
                                        </Grid>
                                      </Grid>
                                    </Box>

                                    <Accordion
                                      disabled={!hasResources}
                                      disableGutters
                                      elevation={0}
                                      sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' }
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={hasResources ? <ExpandMoreRounded /> : null}
                                        sx={{
                                          minHeight: 48,
                                          '& .MuiAccordionSummary-content': { margin: '12px 0' }
                                        }}
                                      >
                                        <Typography
                                          variant='caption'
                                          sx={{
                                            fontWeight: 800,
                                            color: hasResources ? 'primary.main' : 'text.disabled',
                                            letterSpacing: 0.5,
                                            textTransform: 'uppercase'
                                          }}
                                        >
                                          {t_('RESOURCES')} {hasResources ? `(${scopeRow.resources.length})` : ''}
                                        </Typography>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                        <Stack direction='row' spacing={1.5} flexWrap='wrap' useFlexGap>
                                          {scopeRow.resources.map((resource, rIdx) => (
                                            <Chip
                                              key={`${scopeRow.scopeRowId}-res-${rIdx}`}
                                              label={[resource.jobName, [resource.resourceCode, resource.resourceName].filter(Boolean).join(' - ')].filter(Boolean).join(' · ')}
                                              variant='outlined'
                                              sx={{
                                                height: 40,
                                                borderRadius: 1.5,
                                                borderColor: alpha(theme.palette.common.white, 0.15),
                                                backgroundColor: alpha(theme.palette.common.white, 0.04),
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                px: 0.5,
                                                '&:hover': {
                                                  backgroundColor: alpha(theme.palette.common.white, 0.08)
                                                },
                                                '& .MuiChip-label': {
                                                  px: 1.5
                                                }
                                              }}
                                            />
                                          ))}
                                        </Stack>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>
                                )
                              })
                            )}
                      </Stack>
                    )}

                    {detailsTab === 'actions' && (
                      <Stack spacing={1.5}>
                        {isLoadingSelectedOpenIncidenceActions && (
                          <Stack direction='row' alignItems='center' gap={1}>
                            <CircularProgress size={18} />
                            <Typography variant='body2' color='text.secondary'>
                              {t_('Loading actions')}
                            </Typography>
                          </Stack>
                        )}

                        {isLoadingSelectedOpenIncidenceActionsError && (
                          <Typography variant='body2' color='text.secondary'>
                            {t_('Error loading actions')}
                          </Typography>
                        )}

                        {!isLoadingSelectedOpenIncidenceActions && groupedActions.length === 0
                          ? (
                            <Typography variant='body2' color='text.secondary'>
                              {t_('No actions added')}
                            </Typography>
                            )
                          : !isLoadingSelectedOpenIncidenceActions && (
                              groupedActions.map((action) => {
                                const hasChildren = action.children && action.children.length > 0
                                return (
                                  <Box
                                    key={action.actionRowId}
                                    sx={{
                                      border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                                      borderRadius: 2,
                                      backgroundColor: alpha('#000', 0.1),
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <ActionCard action={action} theme={theme} formatDate={formatDate} t_={t_} onExecute={setExecuteAction} onOpenActionDocuments={openActionDocuments} canViewDocuments={canViewActionDocuments} />

                                    {hasChildren && (
                                      <Accordion
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                          backgroundColor: 'transparent',
                                          '&:before': { display: 'none' },
                                          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreRounded />}
                                          sx={{
                                            minHeight: 40,
                                            '& .MuiAccordionSummary-content': { margin: '8px 0' }
                                          }}
                                        >
                                          <Typography
                                            variant='caption'
                                            sx={{
                                              fontWeight: 800,
                                              color: 'primary.main',
                                              letterSpacing: 0.5,
                                              textTransform: 'uppercase'
                                            }}
                                          >
                                            {t_('Sub-actions')} ({action.children.length})
                                          </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                          <Stack spacing={1}>
                                            {action.children.map((child) => (
                                              <ActionCard
                                                key={child.actionRowId}
                                                action={child}
                                                theme={theme}
                                                formatDate={formatDate}
                                                t_={t_}
                                                onExecute={setExecuteAction}
                                                onOpenActionDocuments={openActionDocuments}
                                                canViewDocuments={canViewActionDocuments}
                                                isChild
                                              />
                                            ))}
                                          </Stack>
                                        </AccordionDetails>
                                      </Accordion>
                                    )}
                                  </Box>
                                )
                              })
                            )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
                )
              : (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t_('Select an incidence to see details')}
                  </Typography>
                </Box>
                )}
          </Box>
        )}
      </Box>

      <SmartDialog
        setOpen={showActionDocuments}
        closeCallback={() => setShowActionDocuments(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingActionDocument}
        renderComponent={
          <Document
            reference={{ id: documentsAction.uuid, code: documentsAction.code }}
            resourceType='QUALITY_INCIDENCE_ACTION'
            isDownloadingFile={isDownloadingActionDocument}
            setIsDownloadingFile={setIsDownloadingActionDocument}
            defaultName={documentsAction.code || documentsAction.actionType}
          />
        }
      />

      <SmartDialog
        setOpen={Boolean(executeAction)}
        closeCallback={() => setExecuteAction(null)}
        accept={false}
        cancel={false}
        close
        renderComponent={
          <ActionExecutionDialog
            key={executeAction?.uuid || 'none'}
            action={executeAction}
            onClose={() => setExecuteAction(null)}
            onExecuted={handleActionExecuted}
          />
        }
      />
    </Box>
  )
}

IncidenceListItem.propTypes = {
  incidence: PropTypes.object,
  isSelected: PropTypes.bool.isRequired,
  onSelectUuid: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired,
  priorityColorByValue: PropTypes.instanceOf(Map).isRequired,
  theme: PropTypes.object.isRequired,
  isSmallScreen: PropTypes.bool.isRequired,
  onNavigateToDetails: PropTypes.func.isRequired
}

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  theme: PropTypes.object.isRequired
}

QualityIncidenceHistoryView.propTypes = {
  t_: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  isSmallScreen: PropTypes.bool.isRequired,
  historyPanel: PropTypes.string.isRequired,
  setHistoryPanel: PropTypes.func.isRequired,
  detailsTab: PropTypes.string.isRequired,
  setDetailsTab: PropTypes.func.isRequired,
  historyPanelsHeight: PropTypes.string.isRequired,
  onOpenCreateForm: PropTypes.func.isRequired,
  onOpenEditForm: PropTypes.func.isRequired,
  canEditSelectedDraft: PropTypes.bool.isRequired,
  onCloseDialog: PropTypes.func.isRequired,
  filteredOpenIncidences: PropTypes.array.isRequired,
  selectedOpenIncidence: PropTypes.object,
  isLoadingOpenIncidences: PropTypes.bool.isRequired,
  isLoadingOpenIncidencesError: PropTypes.bool.isRequired,
  onRefreshOpenIncidences: PropTypes.func.isRequired,
  incidenceSearchQuery: PropTypes.string.isRequired,
  onIncidenceSearchQueryChange: PropTypes.func.isRequired,
  onClearIncidenceSearchQuery: PropTypes.func.isRequired,
  priorityFilter: PropTypes.string.isRequired,
  priorityFilterOptions: PropTypes.array.isRequired,
  onPriorityFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  priorityColorByValue: PropTypes.instanceOf(Map).isRequired,
  onSelectOpenIncidenceUuid: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  selectedOpenIncidenceCode: PropTypes.string.isRequired,
  selectedOpenIncidenceLabel: PropTypes.string.isRequired,
  selectedOpenIncidenceStatus: PropTypes.string.isRequired,
  selectedOpenIncidenceStatusColor: PropTypes.string.isRequired,
  selectedOpenIncidencePriority: PropTypes.string.isRequired,
  selectedOpenIncidencePriorityColor: PropTypes.string.isRequired,
  selectedOpenIncidenceCreatedDate: PropTypes.any,
  selectedOpenIncidenceEndDate: PropTypes.any,
  selectedOpenIncidenceFabricationOrderName: PropTypes.string.isRequired,
  selectedOpenIncidenceJobName: PropTypes.string.isRequired,
  selectedOpenIncidenceDeviceName: PropTypes.string.isRequired,
  selectedOpenIncidenceWorkerName: PropTypes.string.isRequired,
  selectedOpenIncidenceResponsibleName: PropTypes.string,
  selectedOpenIncidenceDescription: PropTypes.string.isRequired,
  selectedOpenIncidenceComment: PropTypes.string.isRequired,
  selectedOpenIncidenceProposedSolution: PropTypes.string.isRequired,
  isLoadingSelectedOpenIncidenceDetails: PropTypes.bool.isRequired,
  isLoadingSelectedOpenIncidenceDetailsError: PropTypes.bool.isRequired,
  selectedOpenIncidenceScopeRows: PropTypes.array.isRequired,
  selectedOpenIncidenceActionRows: PropTypes.array.isRequired,
  isLoadingSelectedOpenIncidenceActions: PropTypes.bool.isRequired,
  isLoadingSelectedOpenIncidenceActionsError: PropTypes.bool.isRequired,
  onActionExecuted: PropTypes.func
}

export default QualityIncidenceHistoryView
